import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GuestLoginInput } from '@baerenfang/shared';
import { useAuth } from '../auth/AuthContext';
import { ApiClientError } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';

type Form = { name: string; password: string };

export function Login() {
  const { t } = useTranslation();
  const { loginGuest } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const redirectTo = (loc.state as { from?: string } | null)?.from ?? '/abrechnung';

  // Optional `?name=` prefill — used by the per-name QR codes printed for
  // the hut. Caps length to match the schema and trims whitespace.
  const [params] = useSearchParams();
  const prefilledName = (params.get('name') ?? '').trim().slice(0, 120);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(GuestLoginInput),
    defaultValues: { name: prefilledName, password: '' },
  });

  // When the name is prefilled, send focus to the password field so the
  // guest can type immediately after scanning the QR.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (focusedOnce.current) return;
    if (prefilledName) {
      setFocus('password');
      focusedOnce.current = true;
    }
  }, [prefilledName, setFocus]);

  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (data) => {
    setServerError(null);
    try {
      await loginGuest(data.name, data.password);
      navigate(redirectTo, { replace: true });
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 401) {
        setServerError(t('login.failed'));
      } else if (e instanceof ApiClientError) {
        setServerError(t(`errors.${e.code}`, e.message));
      } else {
        setServerError(t('errors.ERR_INTERNAL'));
      }
    }
  });

  return (
    <section aria-labelledby="login-title" className="max-w-sm mx-auto">
      <h1 id="login-title" className="text-xl sm:text-2xl font-semibold mb-4">
        {t('login.title')}
      </h1>
      <form onSubmit={onSubmit} className="space-y-4 bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 sm:p-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            {t('login.identifier')}
          </label>
          <input
            id="name"
            type="text"
            autoComplete="off"
            spellCheck={false}
            {...register('name')}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{t('errors.ERR_VALIDATION')}</p>
          )}
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            {t('login.password')}
          </label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            {...register('password')}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400"
          />
          {errors.password && (
            <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{t('errors.ERR_VALIDATION')}</p>
          )}
        </div>

        {serverError && (
          <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium py-2.5 disabled:opacity-60"
        >
          {isSubmitting ? t('common.loading') : t('login.submit')}
        </button>
      </form>
    </section>
  );
}
